import { useCallback } from 'react';
import { useWidgetStore } from '../store';
import { ConversationItem, Message } from '../store/types';
import { translateRuntimeError } from '../utils/errorMessages';
import WidgetManager from '../lib/WidgetManager';

export const useMessageHandling = (
  conversation: any,
  conversationId: string | null,
  hasQuota: boolean,
  isWidgetActive: boolean,
  widgetId: string,
  startNewConversation: (text: string) => Promise<string | void>,
  markUserActivity?: () => void
) => {
  const widgetManager = WidgetManager.getInstance();

  const sendMessage = useCallback(
    async (text: string) => {
      if (!hasQuota) {
        useWidgetStore.getState().setError(translateRuntimeError('quota'));
        console.error('[useMessageHandling] Message send blocked - quota exceeded');
        return;
      }

      const activeWidget = widgetManager.getActiveWidgetId();
      if (activeWidget && activeWidget !== widgetId) {
        console.warn(`[useMessageHandling] Message send blocked - widget ${activeWidget} is currently active`);
        return;
      }

      const userMessage: Message = {
        text,
        author: 'user',
        id: Date.now().toString(),
      };

      const conversationItem: ConversationItem = {
        type: 'message',
        id: Date.now().toString(),
        timestamp: Date.now(),
        message: userMessage,
      };

      useWidgetStore.getState().addMessage(userMessage);
      useWidgetStore.getState().addConversationItem(conversationItem);
      markUserActivity?.();
      // Mark that agent is processing (will be cleared on first agent response)
      useWidgetStore.getState().setIsAgentThinking(true);

      try {
        if (!conversationId) {
          // Start a new conversation and then send the message.
          // The conversation object will become active, and we send the message.
          const newConversationId = await startNewConversation(text);
          if (newConversationId) {
            // The message has already been sent as part of starting the conversation with text.
            // In some implementations, you might need to explicitly send it again.
            // For now, we assume startNewConversation handles the first message if provided.
            // The `useConversation` hook's `sendUserMessage` will queue if not connected.
             conversation.sendUserMessage(text);
          }
        } else {
          // Send the message through the active session
          conversation.sendUserMessage(text);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        useWidgetStore.getState().setError(translateRuntimeError(err));
        console.error('[useMessageHandling] Error sending message:', errorMessage);
      }
    },
    [
      conversation,
      conversationId,
      hasQuota,
      widgetManager,
      widgetId,
      startNewConversation,
      markUserActivity,
    ]
  );

  return { sendMessage };
};
